import PropTypes from 'prop-types';
import React from 'react';
import StyledSystemPropTypes, { propType as StyledSystemPropType } from '@styled-system/prop-types';
import { motion } from 'motion/react';

import { Flex, Box } from '../grid';
import { H2, Label, P } from '../typography/new';

const HeadingWithFlameHighlights = ({
  heading,
  color,
  backgroundColor,
  borderRadius,
  mt,
  mb,
  headingLevel: Heading,
  lineHeight,
  textAlign,
  justifyContent,
  isAnimated,
  animationDelay,
  animationIndex,
  disableHighlightAnimation,
  animate,
  // New props for label support
  variant,
  ...rest
}) => {
  if (!heading) {
    return null;
  }

  // Dynamic styling based on heading level
  const headingStylesMap = {
    OversizedH1: {
      borderRadius: { xs: '10.5px', lg: '15px' },
      padding: {
        pt: { xs: '15.5px', lg: '19.5px' },
        pb: { xs: '0.5px', lg: '0.5px' },
        px: { xs: '11px', lg: '16px' },
      },
    },
    H1: {
      borderRadius: { xs: '7.5px', lg: '14.5px' },
      padding: {
        pt: { xs: '11.5px', lg: '19.5px' },
        pb: { xs: '0.5px', lg: '0.5px' },
        px: { xs: '8px', lg: '15px' },
      },
    },
    H2: {
      borderRadius: { xs: '7px', lg: '12.25px' },
      padding: {
        pt: { xs: '11.5px', lg: '17.5px' },
        pb: { xs: '0.5px', lg: '0.5px' },
        px: { xs: '8px', lg: '13px' },
      },
    },
    H3: {
      borderRadius: { xs: '6.25px', lg: '10px' },
      padding: {
        pt: { xs: '9px', lg: '15.5px' },
        pb: { xs: '1px', lg: '0.5px' },
        px: { xs: '6px', lg: '10px' },
      },
    },
    H4: {
      borderRadius: { xs: '5.5px', lg: '7px' },
      padding: {
        pt: { xs: '9px', lg: '11.5px' },
        pb: { xs: '1px', lg: '0.5px' },
        px: { xs: '6px', lg: '7px' },
      },
    },
    // Label variants
    category: {
      borderRadius: { xs: '3.5px', lg: '6.25px' },
      padding: {
        pt: { xs: '7px', lg: '11px' },
        pb: { xs: '1px', lg: '1px' },
        px: { xs: '4px', lg: '6px' },
      },
    },
    section: {
      borderRadius: { xs: '3.5px', lg: '4px' },
      padding: {
        pt: '7px',
        pb: '1px',
        px: '4px',
      },
    },
    // P (paragraph) variants
    'highlight-pill': {
      borderRadius: { xs: '3.25px', lg: '3.75px' },
      padding: {
        pt: { xs: '4px', lg: '3px' },
        pb: { xs: '4px', lg: '5px' },
        px: { xs: '6px', lg: '8px' },
      },
    },
    'highlight-pill-mobile': {
      borderRadius: { xs: '3.25px' },
      padding: {
        pt: { xs: '4px' },
        pb: { xs: '4px' },
        px: { xs: '6px' },
      },
    },
  };

  const getComponent = () => {
    // TODO refactor to look at variable inside of P/Label to determine component
    if (variant === 'highlight-pill' || variant === 'highlight-pill-mobile') {
      return P;
    }
    if (variant === 'category' || variant === 'section') {
      return Label;
    }
    return Heading;
  };

  const getStyleKey = () => {
    if (variant) {
      return variant;
    }
    return Heading?.displayName || Heading?.name || 'H2';
  };

  const Component = getComponent();
  const styleKey = getStyleKey();
  const dynamicStyles = headingStylesMap?.[styleKey] || headingStylesMap.H2;

  const wordAnimationTime = 0.4;
  const highlightTime = 0.4;
  const animationStaggerTime = 0.2;

  const animatedWords = heading.split(' ').map((word, index) => (
    <motion.span
      initial={isAnimated ? 'hidden' : 'visible'}
      key={word}
      style={{ display: 'inline-block' }}
      animate={animate}
      variants={{
        hidden: {
          y: '160%',
        },
        visible: {
          y: 0,
          transition: {
            duration: wordAnimationTime,
            delay: wordAnimationTime * index + animationDelay + animationIndex * animationStaggerTime - 0.2 * index,
            ease: [0.5, 0, 0, 1],
          },
        },
      }}
    >
      {word + (index !== (heading?.split(' ')?.length || 1) - 1 ? ' ' : '')}
    </motion.span>
  ));

  const highlightAnimationInitial = animationIndex !== 2 ? { left: 0 } : { right: 0 };
  const highlightAnimationActive = animationIndex !== 2 ? { right: '0' } : { left: '0' };

  const container = {
    position: 'absolute',
    top: 0,
    bottom: 0,
  };

  if (!isAnimated) {
    container.left = 0;
    container.right = 0;
  }

  return (
    <Flex
      mt={mt}
      mb={mb}
      position="relative"
      justifyContent={justifyContent}
      maxWidth="100%"
      width="fit-content"
      {...rest}
    >
      <Component
        mb="0"
        mt="0"
        zIndex={1}
        pt={dynamicStyles?.padding?.pt}
        pb={dynamicStyles?.padding?.pb}
        px={dynamicStyles?.padding?.px}
        textAlign={textAlign}
        color={color}
        maxWidth="fit-content"
        overflow="hidden"
        style={{ whiteSpace: 'break-spaces', lineHeight: 1 }}
        variant={variant}
        textTransform="none"
      >
        {isAnimated ? animatedWords : heading}
      </Component>

      {!disableHighlightAnimation && (
        <motion.div
          initial={isAnimated ? 'hidden' : 'visible'}
          animate={animate}
          variants={{
            hidden: {
              width: 0,
              ...highlightAnimationInitial,
            },
            visible: {
              width: '100%',
              ...highlightAnimationActive,
              transition: {
                duration: highlightTime,
                ease: [0.5, 0, 0, 1],
                delay:
                  animationDelay +
                  heading.split(' ').length * (wordAnimationTime / 2) +
                  animationIndex * animationStaggerTime,
              },
            },
          }}
          style={container}
        >
          <Box
            width="100%"
            height="100%"
            borderRadius={dynamicStyles?.borderRadius || borderRadius}
            backgroundColor={backgroundColor}
          />
        </motion.div>
      )}
    </Flex>
  );
};

HeadingWithFlameHighlights.propTypes = {
  heading: PropTypes.string.isRequired,
  backgroundColor: StyledSystemPropTypes.color.backgroundColor,
  borderRadius: StyledSystemPropTypes.border.border,
  color: StyledSystemPropTypes.color.color,
  mt: StyledSystemPropType,
  mb: StyledSystemPropType,
  headingLevel: PropTypes.object,
  textAlign: PropTypes.string,
  lineHeight: StyledSystemPropTypes.typography.lineHeight,
  justifyContent: PropTypes.string,
  isAnimated: PropTypes.bool,
  animationDelay: PropTypes.number,
  animationIndex: PropTypes.number,
  disableHighlightAnimation: PropTypes.bool,
  // New props for label support
  variant: PropTypes.string,
  animate: PropTypes.oneOf(['hidden', 'visible']),
};

HeadingWithFlameHighlights.defaultProps = {
  backgroundColor: 'black',
  borderRadius: { xs: '4px', lg: '9px' },
  color: 'white',
  headingLevel: H2,
  mb: '1.5rem',
  mt: null,
  lineHeight: 1,
  textAlign: 'left',
  justifyContent: 'left',
  isAnimated: false,
  animationDelay: 0,
  animationIndex: 0,
  disableHighlightAnimation: false,
  // New defaults for label support
  variant: null,
  animate: 'visible',
};

export { HeadingWithFlameHighlights };
